"use client";

import { useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useSelector } from "react-redux";
import { useGetPublishedMockTestsQuery } from "@/features/api/mockTestApi";

export default function MockTestsPage() {

  const navigate = useNavigate();

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetPublishedMockTestsQuery();

  /* ================= NORMALIZE DATA ================= */

  const user = useSelector((state) => state.auth.user);

 const mockTests = useMemo(() => {
  return Array.isArray(data?.mockTests)
    ? data.mockTests
    : [];
}, [data]);

  /* ================= MEDIA ================= */

  const media = (url) =>
    url?.startsWith("/uploads")
      ? `https://fetc.in${url}`
      : url || "";

  /* ================= ERROR ================= */

  useEffect(() => {

    if (isError) {
      toast.error(error?.data?.message || "Failed to load mock tests");
    }

  }, [isError, error]);

  /* ================= CHECKOUT ================= */

const handleBuyNow = (test) => {

  if (!test?._id) {
    toast.error("Invalid mock test");
    return;
  }

  // 🔐 Redirect to login if not logged in
  if (!user) {
    navigate("/login", {
      state: { redirectTo: `/checkout?id=${test._id}&type=mock` },
    });
    return;
  }

  // ✅ Go to checkout
  navigate(`/checkout?id=${test._id}&type=mock`);
};

  /* ================= LOADING ================= */

  if (isLoading || isFetching) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  /* ================= EMPTY ================= */

  if (!mockTests.length) {
    return (
      <div className="container py-20 text-center text-gray-500">
        No mock tests available.
      </div>
    );
  }

  /* ================= UI ================= */

  return (
    <div className="container px-4 py-10 mx-auto">

      <h1 className="text-3xl font-bold text-center mb-10">
        Mock Tests
      </h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">

        {mockTests.map((test) => (

          <div
            key={test._id}
            className="bg-white rounded-xl shadow-md p-6 flex flex-col justify-between hover:shadow-lg transition"
          >

            <div>

              {test.image && (
                <img
                  src={media(test.image)}
                  alt={test.name}
                  className="w-full h-40 object-cover rounded-lg mb-4"
                />
              )}

              <h2 className="text-xl font-semibold mb-2">
                {test.name}
              </h2>

              <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                {test.description}
              </p>

              <p className="text-lg font-bold text-blue-600">
                ₹{test.price}
              </p>

            </div>

            <Button
          className="mt-4 w-full"
          onClick={() => handleBuyNow(test)}
        >
          {user ? "Buy Now" : "Login to Buy"}
        </Button>

          </div>

        ))}

      </div>

    </div>
  );
}
